/**
 * User identity management functionality
 * Based on Adobe Launch implementation that tracks changes in sessionStorage
 */
import * as amplitude from '@amplitude/analytics-browser';
import { getUserUuid, getVCookie, isWindowDefined } from '@chegg/utils';
import { isHardLoggedIn } from './utils';

/**
 * Get user data from various sources for identity management
 */
function getUserIdentityData() {
  // Get auth status from digitalData or fallback methods
  var getAuthStatus = function getAuthStatus() {
    var _window$digitalData;
    if (typeof window !== 'undefined' && (_window$digitalData = window.digitalData) !== null && _window$digitalData !== void 0 && (_window$digitalData = _window$digitalData.user) !== null && _window$digitalData !== void 0 && (_window$digitalData = _window$digitalData.profile) !== null && _window$digitalData !== void 0 && (_window$digitalData = _window$digitalData.profileInfo) !== null && _window$digitalData !== void 0 && (_window$digitalData = _window$digitalData.segment) !== null && _window$digitalData !== void 0 && _window$digitalData.authStatus) {
      return window.digitalData.user.profile.profileInfo.segment.authStatus;
    }
    // Fallback to checking authentication state from cookies/localStorage
    return isHardLoggedIn() ? 'Hard Logged In' : 'Logged Out';
  };

  // Get subscription status from digitalData or fallback methods
  var getSubscriptionStatus = function getSubscriptionStatus() {
    var _window$digitalData2;
    if (typeof window !== 'undefined' && (_window$digitalData2 = window.digitalData) !== null && _window$digitalData2 !== void 0 && (_window$digitalData2 = _window$digitalData2.user) !== null && _window$digitalData2 !== void 0 && (_window$digitalData2 = _window$digitalData2.profile) !== null && _window$digitalData2 !== void 0 && (_window$digitalData2 = _window$digitalData2.profileInfo) !== null && _window$digitalData2 !== void 0 && _window$digitalData2.csMemberStatus) {
      return window.digitalData.user.profile.profileInfo.csMemberStatus;
    }
    // Fallback to checking subscription indicators
    return 'Unknown';
  };
  return {
    authStatus: getAuthStatus(),
    deviceId: getVCookie() || '',
    userId: getUserUuid() || '',
    cheggVisitorUuid: getVCookie() || '',
    cheggUuid: getUserUuid() || '',
    subscriptionStatus: getSubscriptionStatus()
  };
}

/**
 * Set user identity in Amplitude with change detection
 * Based on Adobe Launch implementation that tracks changes in sessionStorage
 */
export function setAmplitudeUserIdentity() {
  var forceUpdate = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
  if (!isWindowDefined || typeof sessionStorage === 'undefined') {
    console.warn('Cannot set Amplitude user identity: window or sessionStorage not available');
    return;
  }
  try {
    // Get current values from sessionStorage
    var storedAuthStatus = sessionStorage.getItem('auth_status');
    var storedDeviceId = sessionStorage.getItem('device_id');
    var storedUserId = sessionStorage.getItem('user_id');
    var storedSubscriptionStatus = sessionStorage.getItem('subscription_status');
    var storedCheggUuid = sessionStorage.getItem('chegg_uuid');
    var storedCheggVisitorUuid = sessionStorage.getItem('chegg_visitor_uuid');

    // Get new values
    var newData = getUserIdentityData();

    // Check if this is the first time setting identity (no stored values)
    var isFirstTimeSetup = !storedAuthStatus && !storedDeviceId && !storedUserId;
    var shouldSetAll = forceUpdate || isFirstTimeSetup;

    // Create identify event
    var identifyEvent = new amplitude.Identify();
    var shouldUpdate = false;

    // Helper function to update identity property
    var updateIdentityProperty = function updateIdentityProperty(config) {
      // Update if forceUpdate, first time setup, or newValue is different from stored value
      if (shouldSetAll || config.storedValue !== config.newValue) {
        sessionStorage.setItem(config.sessionKey, config.newValue);
        identifyEvent.set(config.key, config.newValue);
        return true;
      }
      return false;
    };

    // Update all identity properties
    shouldUpdate = updateIdentityProperty({
      key: 'auth_status',
      newValue: newData.authStatus,
      storedValue: storedAuthStatus,
      sessionKey: 'auth_status'
    }) || shouldUpdate;
    shouldUpdate = updateIdentityProperty({
      key: 'device_id',
      newValue: newData.deviceId,
      storedValue: storedDeviceId,
      sessionKey: 'device_id'
    }) || shouldUpdate;
    shouldUpdate = updateIdentityProperty({
      key: 'user_id',
      newValue: newData.userId,
      storedValue: storedUserId,
      sessionKey: 'user_id'
    }) || shouldUpdate;
    shouldUpdate = updateIdentityProperty({
      key: 'chegg_visitor_uuid',
      newValue: newData.cheggVisitorUuid,
      storedValue: storedCheggVisitorUuid,
      sessionKey: 'chegg_visitor_uuid'
    }) || shouldUpdate;
    shouldUpdate = updateIdentityProperty({
      key: 'chegg_uuid',
      newValue: newData.cheggUuid,
      storedValue: storedCheggUuid,
      sessionKey: 'chegg_uuid'
    }) || shouldUpdate;
    shouldUpdate = updateIdentityProperty({
      key: 'subscription_status',
      newValue: newData.subscriptionStatus,
      storedValue: storedSubscriptionStatus,
      sessionKey: 'subscription_status'
    }) || shouldUpdate;

    // Only send the identify event if we updated at least one value
    if (shouldUpdate) {
      amplitude.identify(identifyEvent);
    }
  } catch (error) {
    console.error('Error setting Amplitude user identity:', error);
  }
}